/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import * as React from 'react';
import type { PropsWithChildren } from 'react';
import { useCallback, useMemo, useRef, useState } from '@web-stories-wp/react';
import { curatedFontNames } from '@web-stories-wp/fonts';
import type { FontData } from '@src/web-stories-wp/story-editor/src/elements/types';
/**
 * Internal dependencies
 */
import loadStylesheet from '../../utils/loadStylesheet';
import { FONT_WEIGHT_NAMES } from '../../constants';
import Context from './context';
import useLoadFonts from './effects/useLoadFonts';
import useLoadFontFiles from './actions/useLoadFontFiles';
import isCustomFont from './isCustomFont';
import type { FontConfig, FontLoadStatus, FontWeightOption } from './types';
import type { FontCatalogStatus } from './types';

export const GOOGLE_MENU_FONT_URL = 'https://fonts.googleapis.com/css';

function FontProvider({ children }: PropsWithChildren<unknown>) {
  const [loadedFonts, setLoadedFonts] = useState<Omit<FontConfig, 'content'>[]>(
    []
  );
  const [fontLoadStatuses, setFontLoadStatuses] = useState<
    Record<string, FontLoadStatus>
  >({});
  const [fonts, setFonts] = useState<FontData[]>([]);
  const [fontCatalogStatus, setFontCatalogStatus] =
    useState<FontCatalogStatus>('pending');
  const [fontCatalogRetryToken, setFontCatalogRetryToken] = useState(0);
  const [recentFonts, setRecentFonts] = useState<FontData[]>([]);

  useLoadFonts({
    fonts,
    setFonts,
    setFontCatalogStatus,
    retryToken: fontCatalogRetryToken,
  });
  const isFontCatalogLoaded = fontCatalogStatus === 'ready';
  const retryFontCatalog = useCallback(
    () => setFontCatalogRetryToken((token) => token + 1),
    []
  );

  const getFontBy = useCallback(
    (key, value) => {
      const foundFont = fonts.find((thisFont) => thisFont[key] === value);
      if (!foundFont) {
        return {};
      }
      return foundFont;
    },
    [fonts]
  );

  const getFontByName = useCallback(
    (name: string) => {
      return getFontBy('family', name);
    },
    [getFontBy]
  );

  const getFontWeight = useCallback(
    (name: string) => {
      const defaultFontWeights: FontWeightOption[] = [
        { name: FONT_WEIGHT_NAMES[400], value: 400 },
      ];

      const currentFont = getFontByName(name);
      let fontWeights = defaultFontWeights;
      if (currentFont) {
        const { weights } = currentFont;
        if (weights) {
          fontWeights = weights.map((weight) => ({
            name: FONT_WEIGHT_NAMES[weight],
            value: weight,
          }));
        }
      }
      return fontWeights;
    },
    [getFontByName]
  );

  const getFontFallback = useCallback(
    (name: string) => {
      const currentFont = getFontByName(name);
      return currentFont?.fallbacks ? currentFont.fallbacks : [];
    },
    [getFontByName]
  );

  const addRecentFont = useCallback(
    (recentFont: FontData) => {
      const newRecentFonts = [recentFont];
      recentFonts.forEach((font) => {
        if (recentFont.family === font.family) {
          return;
        }
        newRecentFonts.push(font);
      });
      setRecentFonts(newRecentFonts.slice(0, 5));
    },
    [recentFonts]
  );

  const menuFonts = useRef<string[]>([]);
  const ensureMenuFontsLoaded = useCallback((menuFontsRequested: string[]) => {
    const newMenuFonts = menuFontsRequested.filter(
      (fontName) => !menuFonts.current.includes(fontName)
    );
    if (!newMenuFonts?.length) {
      return;
    }
    menuFonts.current = menuFonts.current.concat(newMenuFonts);

    // Create new <link> in head with ref to new font families
    const families = encodeURIComponent(newMenuFonts.join('|'));
    const url = `${GOOGLE_MENU_FONT_URL}?family=${families}&subset=menu&display=swap`;
    loadStylesheet(url, 'web-stories-google-fonts-menu-css').catch(() => {
      // If they failed to load, remove from array again!
      menuFonts.current = menuFonts.current.filter(
        (font) => !newMenuFonts.includes(font)
      );
    });
  }, []);

  const maybeEnqueueFontStyle = useLoadFontFiles({
    setLoadedFonts,
    setFontLoadStatuses,
  });

  const curatedFonts = useMemo(
    () => fonts.filter((font) => curatedFontNames.includes(font.name)),
    [fonts]
  );

  const customFonts = useMemo(
    () => fonts.filter((font) => isCustomFont(font)),
    [fonts]
  );

  const state = {
    state: {
      fonts,
      fontCatalogStatus,
      isFontCatalogLoaded,
      curatedFonts,
      recentFonts,
      customFonts,
      loadedFonts,
      fontLoadStatuses,
    },
    actions: {
      getFontByName,
      maybeEnqueueFontStyle,
      getFontWeight,
      getFontFallback,
      ensureMenuFontsLoaded,
      addRecentFont,
      retryFontCatalog,
    },
  };

  return <Context.Provider value={state}>{children}</Context.Provider>;
}

export default FontProvider;
